import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  DocumentTextIcon,
  PaperAirplaneIcon,
  ScaleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const LawsuitFlowPage: React.FC = () => {
  const { t } = useTranslation();

  const stages = [
    {
      id: 'complaint',
      title: t('lawsuitFlow.complaint.title', 'Send Complaint'),
      icon: DocumentTextIcon,
      description: t('lawsuitFlow.complaint.description', 'First, we send a formal complaint to the accused party, outlining your grievances and giving them an opportunity to resolve the matter outside of court.'),
      features: [
        t('lawsuitFlow.complaint.feature1', 'Professional legal document prepared by AI'),
        t('lawsuitFlow.complaint.feature2', 'Clearly outlines the issues and damages'),
        t('lawsuitFlow.complaint.feature3', 'Gives the accused party opportunity to respond'),
        t('lawsuitFlow.complaint.feature4', 'Sets the foundation for potential resolution')
      ],
      timeline: t('lawsuitFlow.complaint.timeline', '1-2 business days to prepare and send'),
      color: 'blue'
    },
    {
      id: 'demand',
      title: t('lawsuitFlow.demand.title', 'Send Demand Letter'),
      icon: ExclamationTriangleIcon,
      description: t('lawsuitFlow.demand.description', 'If the complaint doesn\'t resolve the issue, we escalate by sending a formal demand letter requesting specific compensation or actions within a set timeframe.'),
      features: [
        t('lawsuitFlow.demand.feature1', 'Escalated formal communication'),
        t('lawsuitFlow.demand.feature2', 'Specific demands with deadlines'),
        t('lawsuitFlow.demand.feature3', 'Legal consequences clearly stated'),
        t('lawsuitFlow.demand.feature4', 'Final opportunity for out-of-court settlement')
      ],
      timeline: t('lawsuitFlow.demand.timeline', '3-5 business days after complaint response period'),
      color: 'yellow'
    },
    {
      id: 'filing',
      title: t('lawsuitFlow.filing.title', 'File Lawsuit'),
      icon: ScaleIcon,
      description: t('lawsuitFlow.filing.description', 'If the demand letter doesn\'t yield results, we file the lawsuit with the appropriate court, officially beginning legal proceedings.'),
      features: [
        t('lawsuitFlow.filing.feature1', 'Official court filing with all documentation'),
        t('lawsuitFlow.filing.feature2', 'Legal service to all parties involved'),
        t('lawsuitFlow.filing.feature3', 'Court scheduling and procedural management'),
        t('lawsuitFlow.filing.feature4', 'Professional legal representation coordination')
      ],
      timeline: t('lawsuitFlow.filing.timeline', '1-2 weeks after demand letter deadline'),
      color: 'green'
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'text-blue-600',
          title: 'text-blue-900',
          text: 'text-blue-800'
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: 'text-yellow-600',
          title: 'text-yellow-900',
          text: 'text-yellow-800'
        };
      case 'green':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: 'text-green-600',
          title: 'text-green-900',
          text: 'text-green-800'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          icon: 'text-gray-600',
          title: 'text-gray-900',
          text: 'text-gray-800'
        };
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${t('lawsuitFlow.title', 'Lawsuit Process Flow')} - tvia.ai`}</title>
        <meta name="description" content={t('lawsuitFlow.metaDescription', 'Understand our three-stage lawsuit process: complaint, demand letter, and court filing. We guide you through each step for the best outcome.')} />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('lawsuitFlow.title', 'Lawsuit Process Flow')}
            </h1>
                          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('lawsuitFlow.subtitle', 'Unlike other platforms that rush you into filing immediately, we guide you through every step of the process. Our strategic three-stage approach maximizes your chances of resolution while minimizing costs and time.')}
              </p>
          </div>

                      {/* Our Guidance Promise */}
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                {t('lawsuitFlow.guidance.title', 'We Guide You Every Step of the Way')}
              </h2>
              <p className="text-lg text-gray-700 text-center mb-6">
                {t('lawsuitFlow.guidance.description', 'Other platforms send you straight to court. We believe in a smarter approach that saves you time, money, and stress.')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-red-600 mb-3">
                    {t('lawsuitFlow.guidance.others.title', 'Other Platforms')}
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• {t('lawsuitFlow.guidance.others.point1', 'Rush you into expensive court filings')}</li>
                    <li>• {t('lawsuitFlow.guidance.others.point2', 'Skip resolution opportunities')}</li>
                    <li>• {t('lawsuitFlow.guidance.others.point3', 'Leave you to navigate alone')}</li>
                    <li>• {t('lawsuitFlow.guidance.others.point4', 'No strategy or guidance')}</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-6 border-2 border-cyan-200">
                  <h3 className="text-lg font-semibold text-cyan-600 mb-3">
                    <span>
                      <span style={{ fontFamily: "'Comfortaa', cursive" }}>
                        {'tvia.ai'}
                      </span>
                      
                      {t('lawsuitFlow.guidance.tvia.title', ' Approach')}
                    </span>
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• {t('lawsuitFlow.guidance.tvia.point1', 'Start with least aggressive approach')}</li>
                    <li>• {t('lawsuitFlow.guidance.tvia.point2', 'Maximize settlement opportunities')}</li>
                    <li>• {t('lawsuitFlow.guidance.tvia.point3', 'Guide you through each stage')}</li>
                    <li>• {t('lawsuitFlow.guidance.tvia.point4', 'Strategic, cost-effective process')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Key Benefits */}
            <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                {t('lawsuitFlow.benefits.title', 'Why Our Staged Approach Works')}
              </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('lawsuitFlow.benefits.cost.title', 'Cost Effective')}
                </h3>
                <p className="text-gray-600">
                  {t('lawsuitFlow.benefits.cost.description', 'Many cases resolve at earlier stages, saving significant legal costs and time.')}
                </p>
              </div>
              <div className="text-center">
                <ClockIcon className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('lawsuitFlow.benefits.time.title', 'Time Efficient')}
                </h3>
                <p className="text-gray-600">
                  {t('lawsuitFlow.benefits.time.description', 'Structured timeline ensures prompt action while allowing reasonable response periods.')}
                </p>
              </div>
              <div className="text-center">
                <ScaleIcon className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('lawsuitFlow.benefits.professional.title', 'Professional')}
                </h3>
                <p className="text-gray-600">
                  {t('lawsuitFlow.benefits.professional.description', 'All communications are legally sound and professionally crafted by AI legal experts.')}
                </p>
              </div>
            </div>
          </div>

          {/* Stages */}
          <div className="space-y-8 mb-12">
            {stages.map((stage, index) => {
              const colors = getColorClasses(stage.color);
              const IconComponent = stage.icon;
              
              return (
                <div key={stage.id} className="relative">
                  <div className={`${colors.bg} ${colors.border} border rounded-lg p-8`}>
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className={`w-16 h-16 ${colors.bg} border-2 ${colors.border} rounded-full flex items-center justify-center`}>
                          <IconComponent className={`h-8 w-8 ${colors.icon}`} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center mb-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
                            {t('common.stage', 'Stage')} {index + 1}
                          </span>
                        </div>
                        <h3 className={`text-2xl font-bold ${colors.title} mb-4`}>
                          {stage.title}
                        </h3>
                        <p className={`text-lg ${colors.text} mb-6`}>
                          {stage.description}
                        </p>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <h4 className={`text-lg font-semibold ${colors.title} mb-3`}>
                              {t('lawsuitFlow.whatWeDoTitle', 'What We Do')}
                            </h4>
                            <ul className="space-y-2">
                              {stage.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start">
                                  <CheckCircleIcon className={`h-5 w-5 ${colors.icon} mt-0.5 mr-2 flex-shrink-0`} />
                                  <span className={`text-sm ${colors.text}`}>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className={`text-lg font-semibold ${colors.title} mb-3`}>
                              {t('lawsuitFlow.timelineTitle', 'Timeline')}
                            </h4>
                            <div className="flex items-start">
                              <ClockIcon className={`h-5 w-5 ${colors.icon} mt-0.5 mr-2 flex-shrink-0`} />
                              <span className={`text-sm ${colors.text}`}>{stage.timeline}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Arrow to next stage */}
                  {index < stages.length - 1 && (
                    <div className="flex justify-center my-6">
                      <ArrowRightIcon className="h-8 w-8 text-gray-400 transform rotate-90" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Important Notes */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <InformationCircleIcon className="h-6 w-6 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-amber-900 mb-2">
                  {t('lawsuitFlow.importantNotes.title', 'Important Notes')}
                </h3>
                <ul className="space-y-2 text-sm text-amber-800">
                  <li>• {t('lawsuitFlow.importantNotes.note1', 'You can choose to start at any stage based on your situation and relationship with the accused party.')}</li>
                  <li>• {t('lawsuitFlow.importantNotes.note2', 'We will verify the accused party\'s contact information before proceeding with any stage.')}</li>
                  <li>• {t('lawsuitFlow.importantNotes.note3', 'Each stage includes automated follow-ups and status tracking.')}</li>
                  <li>• {t('lawsuitFlow.importantNotes.note4', 'Legal filing fees and court costs are separate from our service fees.')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/file-lawsuit"
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
            >
              <PaperAirplaneIcon className="h-6 w-6 mr-2" />
              {t('lawsuitFlow.startYourCase', 'Start Your Case')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default LawsuitFlowPage;